import {UserRole, UserStatus, UserType} from "../API";
import Account from "./Account";
import Model from "./Model";
import UserSetting, {UserSettingType} from "./UserSetting";
import Review from "./Review";
import Session from "./Session";

export enum MFAPreference {
  off = "",
  sms = "sms",
  totp = "totp",
}

export type SubscriptionFrequency = "Monthly" | "Yearly"

class User {
  id: string
  createdAt: string
  updatedAt: string
  userStatus: UserStatus
  userType: UserType
  startDate: string
  endDate: string
  trialEndDate: string
  accountId: string
  account?: Account
  firstName: string
  lastName: string
  email: string
  phone: string
  emailVerified: boolean
  phoneVerified: boolean
  mfaPreference: MFAPreference
  role: UserRole
  advisorAccess: boolean
  lastAccess: string
  customerId: string
  currentModelId: string
  advisorModelId: string
  primaryModelId: string
  advisorPrimaryModelId: string
  currentPlanId: string   // Deprecated
  models: Model[]
  migrateStatus: string
  migratedOn: string
  subscribedOn: string
  cancelledOn: string
  deletedOn: string
  deletedReason: string
  clientOn: string
  sessionCount: number
  settings: UserSetting[]
  referrer: string
  referrerDetails: string
  reviews: Review[]
  sessions: Session[]

  constructor (data: any) {
    this.id = data.id
    this.createdAt = data.createdAt
    this.updatedAt = data.updatedAt
    this.userStatus = data.userStatus
    this.userType = data.userType ?? UserType.Free
    this.accountId = data.accountId
    this.account = data.account ? new Account(data.account) : undefined
    this.firstName = data.firstName
    this.lastName = data.lastName
    this.email = data.email
    this.phone = data.phone
    this.emailVerified = data.emailVerified
    this.phoneVerified = data.phoneVerified
    this.mfaPreference = data.mfaPreference ?? MFAPreference.off
    this.role = data.role
    this.advisorAccess = data.advisorAccess ?? false
    this.lastAccess = data.lastAccess ?? data.updatedAt
    this.customerId = data.customerId
    this.startDate = data.startDate ?? this.createdAt
    this.endDate = data.endDate
    this.trialEndDate = data.trialEndDate
    this.currentModelId = data.currentModelId
    this.advisorModelId = data.advisorModelId
    this.currentPlanId = data.currentPlanId
    this.primaryModelId = data.primaryModelId
    this.advisorPrimaryModelId = data.advisorPrimaryModelId
    this.models = []
    if (data.models && data.models.items) {
      this.loadModels(data.models.items)
    }
    this.migrateStatus = data.migrateStatus
    this.migratedOn = data.migratedOn
    this.subscribedOn = data.subscribedOn
    this.cancelledOn = data.cancelledOn
    this.deletedOn = data.deletedOn
    this.deletedReason = data.deletedReason
    this.clientOn = data.clientOn
    this.referrer = data.referrer
    this.referrerDetails = data.referrerDetails
    this.sessionCount = data.sessionCount ?? 0
    this.settings = []
    if (data.settings && data.settings.items) {
      this.loadSettings(data.settings.items)
    }
    this.reviews = []
    if (data.reviews && data.reviews.items) {
      this.loadReviews(data.reviews.items)
    }
    this.sessions = []
    if (data.sessions && data.sessions.items) {
      this.loadSessions(data.sessions.items)
    }
  }

  loadModels(items: any[]) {
    this.models = items.map((item: any) => new Model(item))
  }

  loadSettings(items: any[]) {
    this.settings = items.map((item: any) => new UserSetting(item))
  }

  loadReviews(items: any[]) {
    this.reviews = items.map((item: any) => new Review(item))
    User.sortReviews(this.reviews)
  }

  static sortReviews(items: Review[]) {
    items.sort((a: Review, b: Review) => b.reviewDate.localeCompare(a.reviewDate))
  }

  loadSessions(items: any[]) {
    this.sessions = items.map((item: any) => new Session(item))
    User.sortSessions(this.sessions)
  }

  static sortSessions(items: Session[]) {
    items.sort((a: Session, b: Session) => a.sessionDate.localeCompare(b.sessionDate))
  }

  get isAdmin(): boolean {
    return this.role === UserRole.Admin
  }

  get isAdvisor(): boolean {
    return this.role === UserRole.Advisor
  }

  get isAdvisorOrAdmin(): boolean {
    return this.role === UserRole.Advisor || this.role === UserRole.Admin
  }

  get isCustomer(): boolean {
    return this.role === UserRole.Customer
  }

  get fullName(): string {
    return `${this.lastName}, ${this.firstName}`
  }

  get initials(): string {
    const names = this.firstName.split(" & ")
    if (names.length === 2) {
      return `${names[0].substring(0, 1)}${names[1].substring(0, 1)}`.toUpperCase()
    } else {
      return `${this.firstName.substring(0, 1)}${this.lastName.substring(0, 1)}`.toUpperCase()
    }
  }

  filterUserSettings(settingType: UserSettingType) {
    return this.settings.filter((setting: UserSetting) => setting.settingType === settingType)
  }

  getUserSetting(settingType: string, name?: string) {
    const setting = this.settings.find((us: UserSetting) => us.settingType === settingType && (name === undefined || us.name === name))
    return setting
  }

  updateUserSetting(userSetting: UserSetting) {
    const index = this.settings.findIndex((setting: UserSetting) => setting.id === userSetting.id)
    if (index >= 0) {
      this.settings[index] = userSetting
    } else {
      this.settings.push(userSetting)
    }
  }

}

export default User